import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  UserPlus, Building2, Users, CalendarClock, Landmark, HeartPulse,
  FileSignature, Cake, ArrowRight, ShieldAlert, CircleCheck, CalendarDays,
} from "lucide-react";
import { Pagina, Indicador, Cartao, Carregando, Falha, variacao } from "../shell/Pagina.js";
import { trpc } from "../lib/trpc.js";
import { data as fData, plural } from "../lib/formato.js";
import * as calendario from "@pessoas/domain/calendario";

const hojeISO = () => new Date().toISOString().slice(0, 10);

/**
 * Abaixo deste quadro, turnover em percentual engana mais do que informa: uma
 * saída em cinco pessoas é 10%, e a mesma saída em duas é 25%.
 */
const PISO_TURNOVER = 10;

type Resumo = Awaited<ReturnType<typeof trpc.painel.resumo.query>>;

/**
 * Painel do DP.
 *
 * A tela responde uma pergunta só: **o que eu preciso fazer hoje para não
 * gerar passivo.** Nada de gráfico decorativo, e nada de número sem o período
 * contra o qual ele está sendo comparado.
 */
export function Painel() {
  const competencia = hojeISO().slice(0, 7);

  const q = useQuery({
    queryKey: ["painel", competencia],
    queryFn: () => trpc.painel.resumo.query({ competencia, diasDeAlerta: 30 }),
  });

  if (q.isPending) {
    return (
      <Pagina secao="Visão geral" titulo="Painel">
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="painel p-4"><Carregando linhas={3} nu /></div>
          ))}
        </div>
      </Pagina>
    );
  }
  if (q.isError) {
    return (
      <Pagina secao="Visão geral" titulo="Painel">
        <Falha erro={q.error} tentarDeNovo={() => void q.refetch()} />
      </Pagina>
    );
  }

  const r = q.data;
  // Base sem ninguém não é "zero pessoas ativas", é base que ainda não foi
  // implantada. Mostrar quatro cartões zerados faz um sistema novo parecer um
  // sistema quebrado, e é a primeira tela que o cliente vê.
  if (r.movimento.atual.ativos === 0 && r.serie.every((m) => m.admissoes === 0)) {
    return <PrimeirosPassos />;
  }
  return <PainelComDados r={r} competencia={competencia} />;
}

function PainelComDados({ r, competencia }: { r: Resumo; competencia: string }) {
  const { atual, anterior, turnover, turnoverAnterior } = r.movimento;
  const alerta = (chave: string) => r.alertas[chave]?.quantidade ?? 0;
  const prazoDe = (chave: string) => r.alertas[chave]?.maisUrgente ?? null;

  const naFila = [
    { chave: "esocial.rejeitado", rotulo: "eSocial rejeitado", icone: ShieldAlert,
      para: "/esocial", severo: true },
    { chave: "esocial.prazo", rotulo: "eSocial com prazo correndo", icone: Landmark,
      para: "/esocial", severo: true },
    { chave: "ferias.dobra", rotulo: "Férias a vencer, risco de dobra", icone: CalendarClock,
      para: "/folha/ferias", severo: true },
    { chave: "experiencia.vencendo", rotulo: "Experiência a vencer", icone: CalendarClock,
      para: "/pessoas", severo: true },
    { chave: "aso.vencido", rotulo: "Exame vencido", icone: HeartPulse,
      para: "/sst/exames", severo: true },
    { chave: "certificado.vencendo", rotulo: "Certificado a expirar", icone: ShieldAlert,
      para: "/configuracoes", severo: true },
    { chave: "aso.aVencer", rotulo: "Exame a vencer em 30 dias", icone: HeartPulse,
      para: "/sst/exames", severo: false },
    { chave: "assinatura.pendente", rotulo: "Documento sem assinatura", icone: FileSignature,
      para: "/pessoas", severo: false },
    { chave: "pendencia.aberta", rotulo: "Pendência aberta", icone: CircleCheck,
      para: "/pessoas", severo: false },
    { chave: "beneficio.divergencia", rotulo: "Divergência em fatura", icone: Users,
      para: "/beneficios/faturas", severo: false },
  ].filter((x) => alerta(x.chave) > 0);

  const severas = naFila.filter((x) => x.severo);

  return (
    <Pagina
      secao="Visão geral" titulo="Painel"
      carimbo={`competência ${competencia}`}
      descricao="O que vence, o que está travado e o que já foi resolvido nesta competência."
    >
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Indicador
          rotulo="Quadro ativo" valor={String(atual.ativos)} atraso={1}
          delta={variacao(atual.ativos, anterior.ativos)}
          periodo={`${anterior.ativos} no fechamento do mês anterior`}
          serie={r.serie.map((m) => m.admissoes - m.desligamentos)}
        />
        <Indicador
          rotulo="Admissões no mês" valor={String(atual.admissoes)} atraso={2}
          delta={variacao(atual.admissoes, anterior.admissoes)}
          periodo={`${anterior.admissoes} no mês anterior`}
          serie={r.serie.map((m) => m.admissoes)}
        />
        <Indicador
          rotulo="Desligamentos no mês" valor={String(atual.desligamentos)} atraso={3}
          delta={variacao(atual.desligamentos, anterior.desligamentos)}
          periodo={`${anterior.desligamentos} no mês anterior`} inverso
          serie={r.serie.map((m) => m.desligamentos)}
        />
        {/* Percentual sobre um quadro minúsculo não é indicador, é ruído: com
            uma pessoa e uma admissão a fórmula devolve 50%, que assusta e não
            informa. Abaixo do piso mostra a contagem absoluta. */}
        {atual.ativos >= PISO_TURNOVER ? (
          <Indicador
            rotulo="Turnover" valor={`${turnover.toFixed(1)}%`} atraso={4}
            delta={variacao(turnover, turnoverAnterior)}
            periodo="média entre entradas e saídas sobre o quadro" inverso
            {...(turnover > 4 ? { sinal: "atencao" as const } : {})}
          />
        ) : (
          <Indicador
            rotulo="Movimento no mês" atraso={4}
            valor={`${atual.admissoes + atual.desligamentos}`}
            detalhe={`Entradas e saídas somadas. O turnover em percentual aparece a partir de ${PISO_TURNOVER} pessoas no quadro.`}
          />
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.35fr_1fr] lg:items-start">
        <Cartao
          titulo="O que precisa de você"
          subtitulo={
            severas.length > 0
              ? `${plural(severas.length, "item com prazo legal", "itens com prazo legal")} atrás`
              : "Nada com prazo legal em aberto"
          }
        >
          {naFila.length === 0 ? (
            <div className="eop-tudo-certo">
              <CircleCheck aria-hidden size={18} />
              <span>Nenhuma pendência aberta nesta competência.</span>
            </div>
          ) : (
            <ul className="eop-fila">
              {naFila.map((x) => {
                const Icone = x.icone;
                const prazo = prazoDe(x.chave);
                return (
                  <li key={x.chave}>
                    <Link to={x.para} className="eop-fila-item">
                      <span aria-hidden className="eop-fila-icone" data-severo={x.severo}>
                        <Icone size={15} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="eop-fila-rotulo">{x.rotulo}</span>
                        {prazo && (
                          <span className="eop-fila-prazo">
                            {/* A data do caso mais crítico, não a contagem: 12
                                pendências sem saber se a primeira vence amanhã
                                não ajuda a priorizar nada. */}
                            mais urgente em {fData(prazo)}
                          </span>
                        )}
                      </span>
                      <span className="num eop-fila-conta">{alerta(x.chave)}</span>
                      <ArrowRight aria-hidden size={14} className="eop-fila-seta" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Cartao>

        <div className="space-y-4">
          <Cartao titulo="Quem está fora hoje" subtitulo="Afastamento em curso">
            <p className="num text-[1.75rem] font-semibold leading-none">
              {alerta("afastado.hoje")}
            </p>
            <p className="mt-2 text-xs" style={{ color: "var(--faint-foreground)" }}>
              {plural(alerta("afastado.hoje"), "pessoa afastada", "pessoas afastadas")}
              {" "}de {atual.ativos} no quadro
            </p>
          </Cartao>

          <Cartao titulo="Datas da semana" subtitulo="Nascimento e tempo de casa">
            {r.aniversarios.length === 0 ? (
              <p className="text-sm" style={{ color: "var(--faint-foreground)" }}>
                Nenhuma data nos próximos 7 dias.
              </p>
            ) : (
              <ul className="eop-datas">
                {r.aniversarios.slice(0, 6).map((a) => (
                  <li key={`${a.pessoaId}-${a.tipo}`}>
                    <Cake aria-hidden size={14} className="eop-datas-icone" />
                    <span className="min-w-0 flex-1 truncate">{a.nome}</span>
                    <span className="eop-datas-info">
                      {a.tipo === "casa" ? `${a.anos} de casa` : `${a.anos} anos`}
                    </span>
                    <span className="num eop-datas-dia">{fData(a.dia).slice(0, 5)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Cartao>
        </div>
      </div>

      <ObrigacoesDoMes competencia={competencia} />

      <div className="grid gap-4 lg:grid-cols-2">
        <Cartao titulo="Quadro por tipo de vínculo" subtitulo="De quem é esta folha">
          <Barras itens={r.porTipo} total={atual.ativos} />
        </Cartao>
        <Cartao titulo="Quadro por estabelecimento"
                subtitulo="A unidade em que a lei opera: RAT, sindicato e feriado">
          <Barras itens={r.porEstabelecimento} total={atual.ativos} />
        </Cartao>
      </div>
    </Pagina>
  );
}

/**
 * Barra proporcional em linha. Escolhida no lugar de rosca porque a pergunta é
 * comparar categorias, e ângulo é pior que comprimento para isso.
 */
function Barras({ itens, total }: { itens: readonly { rotulo: string; quantidade: number }[]; total: number }) {
  if (itens.length === 0) {
    return <p className="text-sm" style={{ color: "var(--faint-foreground)" }}>sem registro</p>;
  }
  const base = Math.max(total, ...itens.map((i) => i.quantidade), 1);
  return (
    <ul className="eop-barras">
      {itens.map((i) => (
        <li key={i.rotulo}>
          <span className="eop-barras-rotulo">{i.rotulo}</span>
          <span className="eop-barras-trilho">
            <span className="eop-barras-preenche"
                  style={{ width: `${Math.max((i.quantidade / base) * 100, 1.5)}%` }} />
          </span>
          <span className="num eop-barras-valor">{i.quantidade}</span>
        </li>
      ))}
    </ul>
  );
}

const PASSOS = [
  { icone: Building2, titulo: "Cadastre o estabelecimento",
    texto: "É a unidade em que a lei opera: o RAT vem do CNAE dele, o sindicato da base territorial e o feriado do município.",
    para: "/configuracoes", acao: "Abrir configurações" },
  { icone: Users, titulo: "Traga as pessoas",
    texto: "A importação valida CPF, PIS e os campos que toda planilha esquece: raça e cor, grau de instrução e nome da mãe. Sem eles o S-2200 é rejeitado em lote.",
    para: "/pessoas", acao: "Importar pessoas" },
  { icone: UserPlus, titulo: "Abra o primeiro vínculo",
    texto: "Admissão, cargo e salário com vigência. É daqui que folha, ponto e eSocial passam a ter o que calcular.",
    para: "/admissao", acao: "Iniciar admissão" },
  { icone: Landmark, titulo: "Ligue o eSocial",
    texto: "Procuração eletrônica no e-CAC, que é o padrão, ou certificado A1 da empresa. Depois o S-1000 e as tabelas.",
    para: "/esocial", acao: "Configurar eSocial" },
];

/**
 * Primeira tela de uma base vazia.
 *
 * Não é enfeite de estado vazio: é a ordem REAL de implantação, e cada passo
 * diz por que ele vem antes do seguinte. Quem chega aqui não sabe que
 * estabelecimento precede pessoa, e descobrir isso no primeiro fechamento é o
 * que faz implantação levar três meses.
 */
function PrimeirosPassos() {
  return (
    <Pagina
      secao="Implantação" titulo="Primeiros passos"
      descricao="A sua base ainda está vazia. Estes quatro passos, nesta ordem, deixam a folha, o ponto e o eSocial prontos para calcular."
    >
      <ol className="eop-passos">
        {PASSOS.map((p, i) => {
          const Icone = p.icone;
          return (
            <li key={p.titulo} className={`painel painel-interativo surge surge-${(i + 1) as 1}`}>
              <div className="eop-passo-topo">
                <span aria-hidden className="eop-passo-numero num">{i + 1}</span>
                <Icone aria-hidden size={16} className="eop-passo-icone" />
              </div>
              <h3 className="eop-passo-titulo">{p.titulo}</h3>
              <p className="eop-passo-texto">{p.texto}</p>
              <Link to={p.para} className="botao mt-4 w-full" data-variante={i === 0 ? "primario" : undefined}>
                {p.acao}
              </Link>
            </li>
          );
        })}
      </ol>

      <ObrigacoesDoMes competencia={new Date().toISOString().slice(0, 7)} />

      <Cartao titulo="Por que esta ordem"
              subtitulo="Pular um passo custa retrabalho, não só tempo">
        <ul className="eop-porque">
          <li><strong>Estabelecimento antes de pessoa:</strong> o vínculo exige um, e trocar depois significa refazer o S-2200 de todo mundo.</li>
          <li><strong>Pessoa antes de vínculo:</strong> readmitir alguém como pessoa nova apaga o histórico de exame, o tempo de casa e os dependentes.</li>
          <li><strong>eSocial por último:</strong> o S-1005 do estabelecimento precisa ser aceito antes do S-2200, senão o vínculo não entra e a competência não fecha.</li>
        </ul>
      </Cartao>
    </Pagina>
  );
}

/**
 * Obrigações do mês.
 *
 * Nenhum SaaS de RH brasileiro traz este calendário para dentro do produto: o
 * DP consome isso como post de blog e PDF de escritório contábil. Aqui a data
 * já vem calculada e ANTECIPADA para dia útil, que é a regra que a intuição
 * erra (cair no sábado significa pagar na sexta, não na segunda).
 */
function ObrigacoesDoMes({ competencia }: { competencia: string }) {
  const hoje = hojeISO();
  const { aVencer, venceram } = calendario.proximasObrigacoes(hoje, 45);
  const [verPassadas, setVerPassadas] = useState(false);
  const proxima = aVencer[0];

  const linha = (o: calendario.Obrigacao, passou: boolean) => {
    const dias = calendario.diasAte(o.vence, hoje);
    return (
      <li key={o.chave + o.vence} data-passou={passou || undefined}>
        <span className="num eop-agenda-dia">{fData(o.vence).slice(0, 5)}</span>
        <span className="min-w-0 flex-1">
          <span className="eop-agenda-nome">{o.nome}</span>
          <span className="eop-agenda-base">{o.base}</span>
        </span>
        <span className="eop-agenda-quando"
              data-tom={passou ? "passou" : dias <= 3 ? "perto" : "longe"}>
          {passou ? "venceu" : dias === 0 ? "hoje" : `${dias}d`}
        </span>
      </li>
    );
  };

  return (
    <Cartao
      titulo="Próximas obrigações"
      subtitulo={
        proxima
          // Sem toLowerCase: ele destrói as siglas, e "transmissão da DCTFWeb"
          // vira "transmissão da dctfweb", que não é o nome de nada.
          ? (() => {
              const d = calendario.diasAte(proxima.vence, hoje);
              return `${proxima.nome}, ${d === 0 ? "hoje" : `em ${plural(d, "dia", "dias")}`}`;
            })()
          : "Nada nos próximos 45 dias"
      }
      acoes={<CalendarDays aria-hidden size={16} style={{ color: "var(--faint-foreground)" }} />}
    >
      <ul className="eop-agenda">
        {aVencer.map((o) => linha(o, false))}
        {verPassadas && venceram.map((o) => linha(o, true))}
      </ul>

      {venceram.length > 0 && (
        <button type="button" className="eop-ver-mais"
                onClick={() => setVerPassadas((v) => !v)}>
          {verPassadas
            ? "esconder o que já venceu"
            : `ver ${plural(venceram.length, "obrigação que já venceu", "obrigações que já venceram")} nesta competência`}
        </button>
      )}
    </Cartao>
  );
}
